import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { request } from '@/utils/request';

interface PaginationState {
  page: number;
  pageSize: number;
  sortBy: string;
  direction: 'asc' | 'desc';
  searchText: string;
}

interface UseTableOptions<TData = any> {
  apiUrl: string; // API endpoint to fetch data from
  initialPaginationState?: Partial<PaginationState>; //Optional: Initial pagination state
  additionalParams?: Record<string, any>; // Optional: Additional parameters to send with API request
  onError?: (error: any) => void; // Optional: Function to handle errors
  onSuccess?: (result: any) => void; // Optional: Function to handle success
  transformResponse?: (result: any) => TData[]; // Optional: Function to transform API response
  dataFetchControl?: {
    shouldTriggerFetch?: () => boolean; // Optional: Boolean to check if data should be fetched to avoid syncing issues
    reFetchData?: boolean; // Optional: Boolean to check if data should be fetched on page change
    setRefetchData?: (value: boolean) => void; // Optional: Function to set the reFetchData state
  };
}

export const useTable = <TData = any>({
  apiUrl,
  initialPaginationState = {
    page: 1,
    pageSize: 10,
    sortBy: 'updated_at',
    direction: 'desc',
    searchText: '',
  },
  additionalParams = {},
  onError,
  onSuccess,
  transformResponse,
  dataFetchControl = {},
}: UseTableOptions<TData>) => {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as Record<string, any>;

  const [data, setData] = useState<TData[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const prevParamsRef = useRef<any>(null);

  // Initialize pagination state from URL parameters (matching old implementation)
  const [paginationState, setPaginationState] = useState<PaginationState>(
    () => ({
      page: searchParams.page ?? initialPaginationState?.page ?? 1,
      pageSize: searchParams.pageSize ?? initialPaginationState?.pageSize ?? 10,
      sortBy:
        searchParams.sortBy ?? initialPaginationState?.sortBy ?? 'updated_at',
      direction:
        searchParams.direction ?? initialPaginationState?.direction ?? 'desc',
      searchText:
        searchParams.searchText ?? initialPaginationState?.searchText ?? '',
    }),
  );

  const { shouldTriggerFetch, reFetchData, setRefetchData } =
    dataFetchControl || {};

  // Reset page to 1 when additionalParams change (e.g., filters)
  const prevAdditionalParamsRef = useRef(additionalParams);
  useEffect(() => {
    const paramsChanged =
      JSON.stringify(prevAdditionalParamsRef.current) !==
      JSON.stringify(additionalParams);

    if (paramsChanged && paginationState.page !== 1) {
      setPaginationState((prev) => ({
        ...prev,
        page: 1,
      }));
    }

    prevAdditionalParamsRef.current = additionalParams;
  }, [additionalParams]);

  // Update URL with pagination state (preserve other search params)
  useEffect(() => {
    const currentSearch = { ...searchParams };
    navigate({
      search: {
        ...currentSearch,
        page: paginationState.page,
        pageSize: paginationState.pageSize,
        sortBy: paginationState.sortBy,
        direction: paginationState.direction,
        searchText: paginationState.searchText || undefined, // Remove from URL if empty
      } as any,
      replace: true,
    });
  }, [paginationState]);

  // Fetch data when pagination state or additionalParams changes
  const fetchData = useCallback(async () => {
    if (!apiUrl || (shouldTriggerFetch && !shouldTriggerFetch())) {
      return;
    }

    const params = {
      page_number: paginationState.page,
      page_size: paginationState.pageSize,
      sort_by: paginationState.sortBy,
      sort_direction: paginationState.direction.toUpperCase(),
      ...(paginationState.searchText && {
        search_text: paginationState.searchText,
      }),
      ...additionalParams,
    };

    // Skip fetch if params haven't changed (unless forced)
    if (
      !reFetchData &&
      JSON.stringify(params) === JSON.stringify(prevParamsRef.current)
    )
      return;

    prevParamsRef.current = params;

    setIsLoading(true);

    try {
      // Use request utility to call API with base URL and auth
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const responseData = await request<any>(
        `${apiUrl}?${queryParams.toString()}`,
      );

      const currentPage = responseData.current_page || 0;
      if (currentPage > 0 && currentPage !== paginationState.page) {
        setPaginationState((prev) => ({
          ...prev,
          page: currentPage,
        }));
        return;
      }

      if (onSuccess) {
        onSuccess(responseData);
      }

      if (responseData) {
        const processedData = transformResponse
          ? transformResponse(responseData)
          : responseData;

        setData(processedData || []);
        setTotalRowsCount(responseData.total_count || 0);
        setPageCount(responseData.page_count || 0);
      } else {
        setData([]);
        setTotalRowsCount(0);
        setPageCount(0);
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
      setData([]);
      setTotalRowsCount(0);
      setPageCount(0);
    } finally {
      if (setRefetchData) {
        setRefetchData(false);
      }
      setIsLoading(false);
    }
  }, [apiUrl, paginationState, additionalParams, reFetchData]);

  // Trigger fetch when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    pageCount,
    totalRowsCount,
    isLoading,
    paginationState,
    setPaginationState,
  };
};
